import React, { useEffect, useState } from 'react'
import { formatDistanceToNowStrict } from 'date-fns'

import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'

// Generic inbox. The server returns title + href per row, so this list
// renderer is one component, no per-kind switch on the client.

const relativeTime = (iso) => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  return formatDistanceToNowStrict(new Date(iso), { addSuffix: true })
}

const Notifications = () => {
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const { getRequest, putpostRequest } = useQuery()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // 'all' | 'unread'

  const updateUnreadCount = (count) => {
    setCurrentUser({ ...currentUser, notificationsUnreadCount: count })
  }

  const fetchList = (status) => {
    setLoading(true)
    // Params go through the params argument, never inline on the URL:
    // getRequest appends its own '?', so an inline query string arrives as
    // `status=unread?` and the controller's exact match never fires.
    const params = status === 'unread' ? { status: 'unread' } : {}
    getRequest('/api/v1/notifications', params, (err, data) => {
      setLoading(false)
      if (err) return
      setItems(data.notifications || [])
      updateUnreadCount(data.unreadCount || 0)
    })
  }

  useEffect(() => { fetchList(filter) }, [filter])

  // Mark one row read. The row dims straight away and rolls back if the POST
  // fails, so nothing waits on the network. `done` runs either way, which is
  // what lets the click-through path navigate even when the write lost.
  const markRead = (n, done) => {
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x))

    putpostRequest(`/api/v1/notifications/${n.id}/mark_read`, 'POST', {}, (err, data) => {
      if (err) {
        setItems(prev => prev.map(x => x.id === n.id ? { ...x, readAt: null } : x))
      } else {
        updateUnreadCount(data.unreadCount)
      }
      if (done) done()
    })
  }

  // Rows are plain anchors, never react-router Links. A notification href is
  // whatever the server decided the destination is, and plenty of those are
  // Rails-rendered pages the SPA router knows nothing about (/courses/:slug,
  // for one). Routing those client-side dropped the user on the SPA's Not
  // Found while the same URL loaded fine on refresh. A full navigation is
  // correct for every href, and it is what the unread branch below already
  // did, so read and unread rows now behave the same.
  const handleClick = (n, e) => {
    if (n.readAt) return // already read; let the browser follow the href
    e.preventDefault()
    // Hold the navigation until the write lands. The destination is a full
    // page load, which tears this component down mid-request otherwise.
    markRead(n, () => { if (n.href) window.location.href = n.href })
  }

  const markAllRead = () => {
    putpostRequest('/api/v1/notifications/mark_all_read', 'POST', {}, (err, data) => {
      if (err) return
      updateUnreadCount(0)
      setItems(prev => prev.map(x => x.readAt ? x : { ...x, readAt: new Date().toISOString() }))
    })
  }

  return (
    <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='flex items-center justify-between flex-wrap gap-3 mb-6'>
        <h1 className='text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100'>Notifications</h1>
        <div className='flex items-center gap-2'>
          <div className='inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-0.5 text-sm'>
            {['all', 'unread'].map(v => (
              <button
                key={v}
                type='button'
                onClick={() => setFilter(v)}
                className={`px-3 py-1 rounded-md font-medium ${filter === v ? 'bg-white dark:bg-slate-900 text-clcpurple shadow-sm' : 'text-slate-600 dark:text-slate-300'}`}
              >
                {v === 'all' ? 'All' : 'Unread'}
              </button>
            ))}
          </div>
          <button
            type='button'
            onClick={markAllRead}
            disabled={(currentUser?.notificationsUnreadCount || 0) === 0}
            className='text-sm font-medium text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark disabled:text-slate-400 disabled:cursor-not-allowed'
          >
            Mark all read
          </button>
        </div>
      </div>

      {loading ? <Loading /> : (
        items.length === 0 ? (
          <div className='py-16 text-center text-slate-500'>
            {filter === 'unread' ? "You're all caught up." : 'No notifications yet.'}
          </div>
        ) : (
          <ul className='divide-y divide-slate-100 dark:divide-slate-800 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden'>
            {/* The Mark read button is a sibling of the anchor, not a child:
                nesting one inside the other is invalid, and the whole row
                would navigate on its click. */}
            {items.map(n => (
              <li key={n.id} className={`flex items-center ${n.readAt ? '' : 'bg-clcpurple/[0.04]'}`}>
                <a
                  href={n.href}
                  onClick={(e) => handleClick(n, e)}
                  className='flex-1 min-w-0 px-4 py-3 hover:bg-slate-900/[0.03] dark:hover:bg-slate-800/50 transition-colors hover:no-underline'
                >
                  <div className='flex items-start gap-3'>
                    {!n.readAt && <span className='mt-1.5 inline-block h-2 w-2 rounded-full bg-clcpurple flex-shrink-0' aria-hidden='true' />}
                    <div className={`min-w-0 flex-1 ${n.readAt ? 'pl-5' : ''}`}>
                      <div className={`text-sm ${n.readAt ? 'text-slate-600 dark:text-slate-300' : 'font-medium text-slate-900 dark:text-slate-100'}`}>
                        {n.title}
                      </div>
                      <div className='text-xs text-slate-500 mt-0.5'>{relativeTime(n.createdAt)}</div>
                    </div>
                  </div>
                </a>
                {!n.readAt && (
                  <button
                    type='button'
                    onClick={() => markRead(n)}
                    title='Mark this notification read'
                    className='flex-shrink-0 mr-3 ml-2 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:text-clcpurple hover:bg-clcpurple/10 dark:text-slate-400 dark:hover:text-white transition-colors whitespace-nowrap'
                  >
                    Mark read
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  )
}

export default Notifications
